import { fmtDate } from "@/lib/dateFormat";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Download, Send, Pencil, Loader2, Hammer, CheckCircle2, XCircle } from "lucide-react";
import { api, money, downloadPdf, garageVatRate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState } from "@/components/States";
import { StatusChip } from "@/components/StatusChip";
import { NumberPlate } from "@/components/NumberPlate";
import { EmailComposeDialog } from "@/components/EmailComposeDialog";
import { WhatsAppSendDialog, WhatsAppButton } from "@/components/WhatsAppSendDialog";
import { WhatsAppLog } from "@/components/WhatsAppLog";
import { waQuoteText } from "@/lib/whatsapp";
import { toast } from "sonner";

function ItemsTable({ heading, items, vatPct }) {
  if (!items.length) return null;
  const vatOn = vatPct > 0;
  return (
    <div className="mb-6" data-testid={`quote-section-${heading.toLowerCase()}`}>
      <p className="mb-2 text-[11px] font-bold tracking-widest text-brand-2">{heading}</p>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b-2 border-midnight text-left text-[10px] uppercase tracking-wide text-muted-foreground">
            <th className="py-2 pr-2">Description</th>
            <th className="py-2 px-2 text-center">Qty</th>
            <th className="py-2 px-2 text-right">Unit</th>
            {vatOn && <th className="py-2 px-2 text-right">VAT</th>}
            <th className="py-2 pl-2 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => {
            const qty = Number(it.qty) || 0;
            const unit = Number(it.unit_price) || 0;
            const n = qty * unit;
            const v = n * (vatPct / 100);
            return (
              <tr key={i} className="border-b border-border">
                <td className="py-3 pr-2 text-midnight">{it.description}</td>
                <td className="py-3 px-2 text-center text-muted-foreground">{qty}</td>
                <td className="py-3 px-2 text-right text-muted-foreground">{money(unit)}</td>
                {vatOn && <td className="py-3 px-2 text-right text-muted-foreground">{money(v)}</td>}
                <td className="py-3 pl-2 text-right font-semibold text-midnight">{money(n + v)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function QuotationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [garage, setGarage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [emailCfg, setEmailCfg] = useState(null);
  const [waOpen, setWaOpen] = useState(false);
  const [waRefresh, setWaRefresh] = useState(0);
  const [apprOpen, setApprOpen] = useState(false);
  const [apprMethod, setApprMethod] = useState("phone");
  const [apprNote, setApprNote] = useState("");
  const [apprSaving, setApprSaving] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const doc = await api(`/quotations/${id}`);
      setQ(doc);
      api("/garage/settings").then(setGarage).catch(() => {});
      if (doc.customer_id) api(`/customers/${doc.customer_id}`).then(setCustomer).catch(() => {});
      if (doc.vehicle_id) api(`/vehicles/${doc.vehicle_id}`).then(setVehicle).catch(() => {});
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const downloadQuotePdf = async () => {
    setDownloading(true);
    try { await downloadPdf(`/quotations/${id}/pdf`, `quotation-${q?.quotation_number || id}.pdf`); }
    catch (e) { toast.error(e.message || "Could not generate PDF"); } finally { setDownloading(false); }
  };
  const sendQuote = () => setEmailCfg({
    endpoint: `/quotations/${id}/send`,
    title: `Email quotation ${q.quotation_number || ""}`.trim(),
    defaultTo: customer?.email || "",
    defaultSubject: `Quotation ${q.quotation_number || ""} from ${garage?.name || ""}`.trim(),
    attachmentLabel: "The quotation",
    submitLabel: "SEND QUOTATION",
  });
  const convertToJob = async () => {
    setConverting(true);
    try {
      const res = await api(`/quotations/${id}/convert-to-job`, { method: "POST" });
      toast.success(res.already_linked ? "Opening the linked job" : "Job created from quotation");
      if (res.job_id) navigate(`/jobs/${res.job_id}`);
    } catch (e) { toast.error(e.message || "Could not convert"); } finally { setConverting(false); }
  };
  const recordApproval = async (decision) => {
    setApprSaving(true);
    try {
      await api(`/quotations/${id}/record-approval`, { method: "POST", body: JSON.stringify({ decision, method: apprMethod, note: apprNote }) });
      toast.success(decision === "approve" ? "Approval recorded" : "Decline recorded");
      setApprOpen(false); setApprNote("");
      load();
    } catch (e) { toast.error(e.message || "Could not record decision"); } finally { setApprSaving(false); }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const items = q.items || [];
  const labour = items.filter((i) => (i.kind || "labour") !== "part");
  const parts = items.filter((i) => (i.kind || "labour") === "part");
  const t = q.totals || {};
  const vatPct = garageVatRate(garage);
  const logoSrc = garage?.logo_data_url || garage?.logo_url;
  const custCompany = customer?.customer_type === "company" || customer?.company_name ? customer?.company_name : null;

  return (
    <div>
      <div className="print:hidden">
        <Link to="/quotations" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-2"><ArrowLeft className="h-4 w-4" />Quotations</Link>
        <PageHeader title={q.quotation_number || "Quotation"} subtitle={q.title}
          actions={<>
            <button onClick={() => window.print()} data-testid="quote-print-btn" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5 text-sm font-bold text-midnight hover:bg-secondary"><Printer className="h-4 w-4" />Print</button>
            <button onClick={downloadQuotePdf} disabled={downloading} data-testid="quote-pdf-btn" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5 text-sm font-bold text-midnight hover:bg-secondary disabled:opacity-50">{downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Download PDF</button>
            <button onClick={sendQuote} data-testid="quote-send-btn" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5 text-sm font-bold text-midnight hover:bg-secondary"><Send className="h-4 w-4" />Email to Customer</button>
            <WhatsAppButton onClick={() => setWaOpen(true)} label="WhatsApp" testId="quote-whatsapp-btn" />
            <button onClick={() => navigate(`/quotations/${id}/edit`)} data-testid="quote-edit-btn" className="inline-flex items-center gap-2 rounded-lg bg-brand px-3 py-2.5 text-sm font-bold text-white"><Pencil className="h-4 w-4" />Edit</button>
            {q.job_id ? (
              <button onClick={() => navigate(`/jobs/${q.job_id}`)} data-testid="quote-view-job-btn" className="inline-flex items-center gap-2 rounded-lg bg-midnight px-3 py-2.5 text-sm font-bold text-white"><Hammer className="h-4 w-4" />View Job</button>
            ) : (
              <>
                {q.status !== "accepted" && q.status !== "rejected" && (
                  <button onClick={() => setApprOpen(true)} data-testid="quote-record-approval-btn" className="inline-flex items-center gap-2 rounded-lg border border-success bg-success/10 px-3 py-2.5 text-sm font-bold text-success hover:bg-success/20"><CheckCircle2 className="h-4 w-4" />Record Approval</button>
                )}
                <button onClick={convertToJob} disabled={converting || q.status !== "accepted"} title={q.status !== "accepted" ? "The customer must approve this quotation first" : ""} data-testid="quote-convert-btn" className="inline-flex items-center gap-2 rounded-lg bg-midnight px-3 py-2.5 text-sm font-bold text-white disabled:opacity-50">{converting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Hammer className="h-4 w-4" />}Create Job</button>
              </>
            )}
          </>} />
      </div>

      {q.approval && (
        <div data-testid="quote-approval-stamp" className={`mx-auto mb-4 max-w-3xl rounded-xl border p-4 ${q.approval.status === "approved" ? "border-success/40 bg-success/10" : "border-danger/40 bg-danger-bg"}`}>
          <div className="flex items-center gap-2">
            {q.approval.status === "approved" ? <CheckCircle2 className="h-5 w-5 text-success" /> : <XCircle className="h-5 w-5 text-danger" />}
            <span className={`font-display text-lg font-bold uppercase ${q.approval.status === "approved" ? "text-success" : "text-danger"}`}>
              Customer {q.approval.status === "approved" ? "Approved" : "Declined"}
            </span>
            {q.approval.amount != null && <span className="ml-auto font-bold text-midnight">{money(q.approval.amount)}</span>}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {fmtDate(q.approval.decided_at)} · via {String(q.approval.method || "").replace("_", "-")}
            {q.approval.recorded_by_name ? ` · ${q.approval.recorded_by_name}` : ""}
            {q.approval.note ? ` · "${q.approval.note}"` : ""}
          </p>
        </div>
      )}

      {/* A4 document */}
      <div className="mx-auto max-w-3xl rounded-xl border border-border bg-card p-10 shadow-sm print:border-0 print:p-6 print:shadow-none" data-testid="quote-doc">
        <div className="flex items-start justify-between gap-6 border-b-2 border-midnight pb-6">
          <div>
            {logoSrc && <img src={logoSrc} alt="logo" className="mb-3 h-14" data-testid="quote-logo" />}
            <h2 className="font-display text-2xl font-bold uppercase text-midnight">{garage?.name || "—"}</h2>
            {garage?.address && <p className="mt-1 text-xs text-muted-foreground">{garage.address}</p>}
            {(garage?.phone || garage?.email) && <p className="text-xs text-muted-foreground">{[garage?.phone, garage?.email].filter(Boolean).join(" · ")}</p>}
            {garage?.vat_number && <p className="mt-1 text-xs font-bold text-midnight">VAT No: {garage.vat_number}</p>}
          </div>
          <div className="text-right">
            <p className="font-display text-3xl font-bold uppercase text-brand">Quotation</p>
            <p className="text-sm font-bold text-midnight">{q.quotation_number}</p>
            <div className="mt-2 flex justify-end"><StatusChip status={q.status || "draft"} /></div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 py-7 text-sm">
          <div>
            <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Quotation To</p>
            {custCompany && <p className="font-bold text-midnight">{custCompany}</p>}
            <p className={custCompany ? "text-muted-foreground" : "font-bold text-midnight"}>{customer?.name || "—"}</p>
            {customer?.address && <p className="text-muted-foreground">{customer.address}</p>}
            {customer?.phone && <p className="text-muted-foreground">{customer.phone}</p>}
            {customer?.email && <p className="text-muted-foreground">{customer.email}</p>}
            {customer?.vat_registered && customer?.vat_number && <p className="mt-1 text-xs font-semibold text-midnight">VAT: {customer.vat_number}</p>}
          </div>
          <div className="text-right">
            <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Details</p>
            <p className="text-muted-foreground">Date: <span className="font-semibold text-midnight">{fmtDate(q.created_at)}</span></p>
            <p className="text-muted-foreground">Valid until: <span className="font-semibold text-midnight">{fmtDate(q.valid_until)}</span></p>
            {vehicle && (
              <div className="mt-3 flex flex-col items-end gap-1">
                <NumberPlate vrm={vehicle.vrm} size="sm" />
                <p className="text-xs font-semibold text-midnight">{vehicle.make} {vehicle.model}</p>
                <p className="text-[11px] text-muted-foreground">{[vehicle.year, vehicle.colour, vehicle.fuel_type].filter(Boolean).join(" · ")}</p>
              </div>
            )}
          </div>
        </div>

        {q.assessment_notes && (
          <div className="mb-5" data-testid="quote-assessment-display">
            <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Assessment Notes</p>
            <p className="whitespace-pre-line text-sm text-slate-700">{q.assessment_notes}</p>
          </div>
        )}
        {q.title && (
          <div className="mb-5 border-b border-border pb-4">
            <p className="mb-0.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Work Category</p>
            <p className="font-display text-lg font-bold text-midnight" data-testid="quote-workcategory-display">{q.title}</p>
          </div>
        )}

        <ItemsTable heading="Labour" items={labour} vatPct={vatPct} />
        <ItemsTable heading="Parts" items={parts} vatPct={vatPct} />

        <div className="mt-6 flex justify-end">
          <div className="w-64 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Net</span><span className="font-semibold text-midnight">{money(t.net)}</span></div>
            {vatPct > 0 && <div className="flex justify-between"><span className="text-muted-foreground">VAT ({vatPct}%)</span><span className="font-semibold text-midnight">{money(t.vat)}</span></div>}
            <div className="flex justify-between border-t-2 border-midnight pt-2 text-lg"><span className="font-bold text-midnight">Grand Total</span><span className="font-display font-bold text-brand">{money(t.gross)}</span></div>
          </div>
        </div>

        {q.notes && (
          <div className="mt-8 rounded-lg bg-secondary p-4">
            <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Notes</p>
            <p className="whitespace-pre-line text-sm text-slate-700">{q.notes}</p>
          </div>
        )}

        {q.important_note && (
          <div className="mt-6 rounded-lg border-2 border-midnight bg-secondary p-4" data-testid="quote-important-display">
            <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-midnight">Important Note</p>
            <p className="whitespace-pre-line text-sm font-medium text-slate-800">{q.important_note}</p>
          </div>
        )}

        <p className="mt-8 border-t border-border pt-4 text-center text-[11px] text-muted-foreground">
          This quotation is an estimate and valid until the date shown. Prices include VAT where applicable. Work will not commence until approved.
        </p>
      </div>
      <EmailComposeDialog cfg={emailCfg} onClose={() => setEmailCfg(null)} onSent={load} />
      <WhatsAppLog entityType="quotation" entityId={q.id} refreshKey={waRefresh} />
      <WhatsAppSendDialog
        open={waOpen} onClose={() => setWaOpen(false)} onSent={() => setWaRefresh((x) => x + 1)}
        customerId={customer?.id} customerName={customer?.name} to={customer?.phone}
        defaultText={waQuoteText(q, { customerName: customer?.name, garageName: garage?.name })}
        kind="quotation_link" entityType="quotation" entityId={q.id}
      />
      {apprOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 print:hidden" data-testid="record-approval-modal">
          <div className="w-full max-w-md rounded-xl bg-card p-5 shadow-xl">
            <h3 className="font-display text-lg font-bold uppercase text-midnight">Record Customer Decision</h3>
            <p className="mt-1 text-xs text-muted-foreground">Log a genuine decision the customer gave you (phone, in person, email or in writing). This is attested by you.</p>
            <label className="mt-3 block text-[11px] font-bold tracking-widest text-muted-foreground">METHOD</label>
            <select value={apprMethod} onChange={(e) => setApprMethod(e.target.value)} data-testid="appr-method" className="mt-1 w-full rounded-lg border border-input bg-card px-3 py-2.5 text-sm outline-none focus:border-brand">
              <option value="phone">Phone</option>
              <option value="in_person">In person</option>
              <option value="email">Email</option>
              <option value="written">Written</option>
            </select>
            <label className="mt-3 block text-[11px] font-bold tracking-widest text-muted-foreground">NOTE (OPTIONAL)</label>
            <textarea value={apprNote} onChange={(e) => setApprNote(e.target.value)} rows={2} data-testid="appr-note" placeholder="e.g. Confirmed by John on 09/06 at 14:30" className="mt-1 w-full rounded-lg border border-input bg-card px-3 py-2 text-sm outline-none focus:border-brand" />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button onClick={() => setApprOpen(false)} className="rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-bold text-midnight hover:bg-secondary">Cancel</button>
              <button onClick={() => recordApproval("decline")} disabled={apprSaving} data-testid="appr-decline" className="inline-flex items-center gap-2 rounded-lg border border-danger bg-danger-bg px-4 py-2.5 text-sm font-bold text-danger disabled:opacity-60"><XCircle className="h-4 w-4" />Declined</button>
              <button onClick={() => recordApproval("approve")} disabled={apprSaving} data-testid="appr-approve" className="inline-flex items-center gap-2 rounded-lg bg-success px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60">{apprSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}Approved</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
